<script setup lang="ts">
import { dateFormatter } from '@/helpers/ep/formatters'
import type { IRSAFilters } from '@/types/rc/filters'
import { getRSAVersionDocument, getRSAs } from '@/services/rc.service'
import { useQuery } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import { reactive, ref } from 'vue'
import { Icon } from '@iconify/vue'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import CreateRSADialog from '../customer-details/rsa/CreateRSADialog.vue'

const props = defineProps<{ customerId: number }>()

const filters = reactive<IRSAFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  customer_id: props.customerId,
})

const createRSADialogOpen = ref<boolean>(false)
const newVersionTarget = ref<any>()

const { isFetching, isError, isRefetching, data, error, refetch } = useQuery({
  queryKey: ['customer-rsa', filters],
  queryFn: () => getRSAs(filters),
})

/**
 * Opens "create RSA dialog" in "create new version" mode.
 */
const addNewRSAVersion = async (rsaDetails: any) => {
  newVersionTarget.value = rsaDetails
  createRSADialogOpen.value = true
}

/**
 * Opens a repair service agreement in a new tab.
 *
 * @param documentId the ID of the document to open.
 */
const handleDownload = async (rsaVersionNumber: string) => {
  let newTab
  try {
    newTab = window.open()
    if (!newTab) {
      ElMessage({
        type: 'error',
        message: 'Failed to open a new tab, check your browser settings.',
      })
      return
    }
    newTab.document.write('Loading RSA document...')
    const data = await getRSAVersionDocument(rsaVersionNumber)
    const fileUrl = window.URL.createObjectURL(data)
    newTab.location.assign(fileUrl)
    URL.revokeObjectURL(fileUrl)
  } catch {
    ElMessage({
      type: 'error',
      message: 'Failed to retrieve RSA version document.',
    })
    newTab?.close()
  }
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <el-table
      v-loading="isFetching"
      :data="data?.data"
      :stripe="true"
      :empty-text="isFetching ? 'Loading...' : 'No data.'"
      row-key="number"
      :tree-props="{ hasChildren: 'hasChildren', children: 'versions' }"
      default-expand-all
    >
      <el-table-column label="Number" prop="number" :min-width="192" />
      <el-table-column label="Start Date" prop="start_date" :formatter="dateFormatter" :min-width="192" />
      <el-table-column label="Expiration Date" prop="expiration_date" :formatter="dateFormatter" :min-width="192" />
      <el-table-column label="Status" :min-width="112">
        <template #default="scope">
          <StatusBadge :status="scope.row.status" />
        </template>
      </el-table-column>
      <el-table-column :min-width="192">
        <template #default="scope">
          <el-button
            v-if="scope.row.versions && !scope.row.versions.map((v: any) => v.status).includes('Pending')"
            type="primary"
            @click="
              addNewRSAVersion({ number: scope.row.number, expired: scope.row.last_version.status === 'Expired' })
            "
            >Add New Version</el-button
          >
          <el-button v-else-if="scope.row.version" link type="primary" @click="handleDownload(scope.row.number)">
            <template #icon>
              <Icon icon="material-symbols:download-rounded" height="20" width="20" />
            </template>
            <template #default> Document </template>
          </el-button>
        </template>
      </el-table-column>
      <template #empty>
        <span v-if="isFetching">Loading...</span>
        <div v-else class="rsa__add-new">
          <span>Customer has no previous RSA(s)</span>
          <el-button type="primary" @click="createRSADialogOpen = true">Add new RSA</el-button>
        </div>
      </template>
    </el-table>
    <el-pagination
      v-if="data"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="rsa__pagination"
      background
      :layout="DEFAULT_PAGINATION_LAYOUT"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      hide-on-single-page
    />
    <CreateRSADialog
      v-model="createRSADialogOpen"
      :customer-id="customerId"
      :rsa-details="newVersionTarget"
      @dialog-closed="newVersionTarget = undefined"
    />
  </template>
</template>

<style scoped lang="scss">
.rsa {
  &__header {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
  }

  &__add-new {
    padding: var(--spacing-spacing-lg);
    line-height: 20px;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-spacing-sm);
    justify-content: center;
    align-items: center;
  }

  &__pagination {
    align-self: flex-end;
  }
}
</style>
